import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { HubEditorial } from "@/components/site/HubEditorial";
import { HUB_EDITORIAL } from "@/data/hub-editorial";
import { socialImageMeta } from "@/lib/social";
import { ShareBar } from "@/components/site/ShareBar";
import { makeSeoDescription } from "@/lib/seo-description";

const CANONICAL = "https://lifewestway.com/investment-money-transfer";
const TITLE = "Investment & Money Transfer 2026 | LifeWestWay";

export const Route = createFileRoute("/investment-money-transfer")({
  head: () => ({
    meta: [
      { title: TITLE },
      {
        name: "description",
        content:
          makeSeoDescription("How expats invest and send money abroad in 2026 — brokerage accounts, retirement savings, remittance costs and the cheapest transfer routes for the USA, UK, Australia, Canada, Italy and Portugal."),
      },
      { property: "og:title", content: TITLE },
      { property: "og:site_name", content: "LifeWestWay" },
      {
        property: "og:description",
        content:
          makeSeoDescription("Investing as a newcomer and moving money across borders: fees, tax treatment and safe transfer options across six Western destinations."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      ...socialImageMeta(),
      { name: "twitter:title", content: TITLE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: InvestmentMoneyTransferHub,
});

type Guide = {
  country: string;
  flag: string;
  accent: string;
  title: string;
  blurb: string;
  highlight: string;
  href: string;
};

const GUIDES: Guide[] = [
  {
    country: "United States",
    flag: "🇺🇸",
    accent: "from-sky-500/25 to-indigo-400/10",
    title: "Investing & Sending Money from the USA (2026)",
    blurb:
      "Brokerage and 401(k) basics for newcomers, FDIC-protected savings, and the cheapest ways to remit dollars home.",
    highlight: "Brokerage · 401(k) · remittance",
    href: "/usa",
  },
  {
    country: "United Kingdom",
    flag: "🇬🇧",
    accent: "from-indigo-500/25 to-sky-400/10",
    title: "Investing & Money Transfer from the UK (2026)",
    blurb:
      "ISAs, workplace pensions and FSCS-protected accounts — plus low-fee GBP transfer routes for expats.",
    highlight: "ISA · pension · GBP transfers",
    href: "/uk",
  },
  {
    country: "Australia",
    flag: "🇦🇺",
    accent: "from-amber-500/25 to-emerald-400/10",
    title: "Investing & Sending Money from Australia (2026)",
    blurb:
      "Superannuation, ASX investing for residents, and how to move AUD abroad without losing money on spreads.",
    highlight: "Super · ASX · AUD transfers",
    href: "/australia",
  },
  {
    country: "Canada",
    flag: "🇨🇦",
    accent: "from-red-500/25 to-rose-400/10",
    title: "Investing & Money Transfer from Canada (2026)",
    blurb:
      "TFSA and RRSP essentials for newcomers, CDIC-covered deposits, and the best CAD remittance options.",
    highlight: "TFSA · RRSP · CAD transfers",
    href: "/canada",
  },
  {
    country: "Italy",
    flag: "🇮🇹",
    accent: "from-emerald-500/25 to-rose-500/10",
    title: "Investing & Sending Money from Italy (2026)",
    blurb:
      "Italian investment accounts, capital gains treatment, SEPA transfers and FITD-protected deposits.",
    highlight: "SEPA · capital gains · FITD",
    href: "/italy",
  },
  {
    country: "Portugal",
    flag: "🇵🇹",
    accent: "from-emerald-500/25 to-amber-400/10",
    title: "Investing & Money Transfer from Portugal (2026)",
    blurb:
      "Investing as a D7/D8 resident, IRS treatment of investment income, and low-cost EUR transfer routes.",
    highlight: "D7/D8 · IRS · EUR transfers",
    href: "/portugal",
  },
];

function InvestmentMoneyTransferHub() {
  return (
    <main id="main-content" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs
        items={[{ label: "Home", href: "/" }, { label: "Investment & Money Transfer" }]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 via-card/40 to-background/60 p-8 sm:p-12">
        <span
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/20 blur-3xl"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl"
        />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
            6 Countries · Updated 2026
          </span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Investment & <span className="text-accent">Money Transfer</span> for Expats
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-foreground/75">
            Where newcomers can invest legally, how investment income is taxed, and the cheapest,
            safest ways to move money across borders in each of our six destinations.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section
        aria-label="Investment and money transfer guides by country"
        className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
      >
        {GUIDES.map((g) => (
          <Link title={`Read the investment and money transfer guide: ${g.title}`}
            key={g.title}
            to={g.href}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/50 p-6 transition-all hover:border-accent/60 hover:bg-card/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span
              aria-hidden
              className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${g.accent} opacity-0 transition-opacity group-hover:opacity-100`}
            />
            <div className="relative flex items-center justify-between">
              <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/40 px-3 py-1 text-xs font-semibold text-foreground/80">
                <span aria-hidden className="text-base leading-none">
                  {g.flag}
                </span>
                {g.country}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent/90">
                2026
              </span>
            </div>

            <h2 className="relative mt-4 font-display text-xl font-bold tracking-tight text-foreground group-hover:text-accent">
              {g.title}
            </h2>
            <p className="relative mt-3 text-sm text-foreground/70">{g.blurb}</p>

            <div className="relative mt-5 flex items-center justify-between border-t border-border/50 pt-4">
              <span className="text-xs font-medium text-foreground/60">
                <span className="text-accent">◆</span> {g.highlight}
              </span>
              <span className="text-sm font-semibold text-accent">
                Explore <span aria-hidden>→</span>
              </span>
            </div>
          </Link>
        ))}
      </section>

      <HubEditorial data={HUB_EDITORIAL["investment-money-transfer"]} />

      <ShareBar />
    </main>
  );
}
